import styled, { css } from 'styled-components';
import { Dialog } from '@mui/material';

import breakpoints from 'theme/atoms/breakpoints';
import colours from 'theme/atoms/colours';
import shadows from 'theme/atoms/shadows';
import typography from 'theme/atoms/typography';
import * as Buttons from 'theme/components/Buttons.style';
import * as Text from 'theme/components/Text.style';
import { spin } from 'shiftNotes/styles/shared.style';
import { skeletonPulseAnimation } from 'shared/_styles/Skeleton.style';

const CARD_SHADOW =
  '0px 0px 0px 0.5px rgba(0, 0, 0, 0.03), 0px 20px 80px -15px rgba(0, 29, 37, 0.1)';

const CARD_RADIUS_PX = 30;
const CARD_PADDING_PX = 15;
const CARD_GAP_PX = 15;

export const Card = styled('div')`
  background: ${colours.shade[0]};
  border-radius: ${CARD_RADIUS_PX}px;
  box-shadow: ${CARD_SHADOW};
  display: flex;
  flex-direction: column;
  gap: ${CARD_GAP_PX}px;
  padding: ${CARD_PADDING_PX}px;
`;

export const Header = styled('div')`
  align-items: flex-start;
  display: flex;
  gap: 10px;
  justify-content: space-between;
`;

export const HeaderMeta = styled('div')`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 5px;
  justify-content: flex-end;
`;

export const HeaderAuthor = styled('div')`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
`;

export const MenuWrapper = styled('div')`
  & > div {
    min-width: 0;
    min-height: 0;
  }
`;

export const MenuButton = styled('button')`
  align-items: center;
  appearance: none;
  background: transparent;
  border: none;
  color: ${colours.neutral[900]};
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 30px;
  justify-content: center;
  padding: 0;
  width: 30px;

  svg {
    height: 18px;
    width: 18px;
  }

  svg path {
    stroke: currentColor;
  }
`;

export const EditTextArea = styled('textarea')`
  ${typography.p2}
  background: transparent;
  border: none;
  color: ${colours.neutral[900]};
  outline: none;
  resize: none;
  width: 100%;
  word-break: break-word;

  &::placeholder {
    color: ${colours.neutral[400]};
  }
`;

export const EditActionRow = styled('div')`
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

export const HeaderIcon = styled('div')<{
  $color?: string;
  $strokeWidth?: number;
}>`
  align-items: center;
  color: ${({ $color }) => $color ?? colours.neutral[500]};
  display: flex;
  flex-shrink: 0;
  height: 16px;
  justify-content: center;
  width: 16px;

  svg {
    height: 16px;
    stroke-width: ${({ $strokeWidth }) => $strokeWidth ?? 2};
    width: 16px;
  }
`;

export const MetaSeparator = styled('span')`
  background: ${colours.neutral[500]};
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
  height: 4px;
  width: 4px;
`;

export const AuthorName = styled(Text.LabelSemibold)`
  color: ${colours.neutral[900]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const TimeLabel = styled(Text.Label)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const HeaderTimeRow = styled('div')`
  align-items: center;
  display: flex;
  gap: 6px;
  min-width: 0;
`;

export const TranslationToggle = styled('button')`
  ${typography.label}
  align-items: center;
  background: none;
  border: none;
  color: ${colours.neutral[500]};
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  gap: 3px;
  padding: 0;

  svg {
    height: 12px;
    width: 12px;
  }

  &:hover {
    color: ${colours.neutral[600]};
    text-decoration: underline;
  }
`;

export const Body = styled(Text.P2)`
  word-break: break-word;
`;

export const StackContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

export const CardSlot = styled('div')`
  &[role='button'] {
    cursor: pointer;
  }

  &[role='button']:focus-visible {
    border-radius: ${CARD_RADIUS_PX}px;
    outline: 2px solid ${colours.primary[500]};
    outline-offset: 4px;
  }
`;

const ATTACHMENT_RADIUS_PX = 15;
const ON_MEDIA_SHADOW = 'rgba(0, 0, 0, 0.75)';

export const AttachmentGalleryFrame = styled('div')`
  margin-right: -${CARD_PADDING_PX}px;
  position: relative;
`;

export const AttachmentGallery = styled('div')`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-right: ${CARD_PADDING_PX}px;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const AttachmentScrollButton = styled(Buttons.SecondaryLightIcon)<{
  $position: 'previous' | 'next';
}>`
  box-shadow: ${shadows.S};
  ${({ $position }) => ($position === 'previous' ? 'left' : 'right')}: 10px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);

  @media (hover: none) {
    display: none;
  }
`;

export const AttachmentTile = styled('div')<{
  $isSingle: boolean;
  $isLoading: boolean;
}>`
  ${({ $isLoading }) => $isLoading && skeletonPulseAnimation}
  aspect-ratio: 3 / 2;
  background: ${colours.neutral[200]};
  border-radius: ${ATTACHMENT_RADIUS_PX}px;
  cursor: pointer;
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
  width: ${({ $isSingle }) => ($isSingle ? '100%' : '200px')};

  &:focus-visible {
    outline: 2px solid ${colours.primary[500]};
    outline-offset: 2px;
  }
`;

const attachmentMedia = css<{ $isLoading: boolean }>`
  display: block;
  height: 100%;
  object-fit: cover;
  opacity: ${({ $isLoading }) => ($isLoading ? 0 : 1)};
  pointer-events: none;
  transition: opacity 0.2s ease-in;
  width: 100%;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const AttachmentImage = styled('img')<{ $isLoading: boolean }>`
  ${attachmentMedia}
`;

export const AttachmentVideo = styled('video')<{ $isLoading: boolean }>`
  ${attachmentMedia}
`;

export const AttachmentCameraBadge = styled('span')`
  color: ${colours.shade[0]};
  display: flex;
  filter: drop-shadow(0 1px 2px ${ON_MEDIA_SHADOW})
    drop-shadow(0 0 4px ${ON_MEDIA_SHADOW});
  left: 10px;
  pointer-events: none;
  position: absolute;
  top: 10px;

  svg {
    height: 18px;
    width: 18px;
  }

  svg path {
    stroke: currentColor;
  }
`;

const PLAY_ICON_OPTICAL_OFFSET_PX = 1.5;

export const AttachmentOpenIndicator = styled('span')<{ $isPlayIcon: boolean }>`
  align-items: center;
  background-color: ${colours.neutral[200]};
  border-radius: 100px;
  box-shadow: ${shadows.S};
  color: ${colours.neutral[900]};
  display: flex;
  height: 50px;
  justify-content: center;
  left: 50%;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%) scale(0.9);
  transition: opacity 0.15s ease, transform 0.15s ease;
  width: 50px;

  svg {
    height: 20px;
    transform: translateX(
      ${({ $isPlayIcon }) => ($isPlayIcon ? PLAY_ICON_OPTICAL_OFFSET_PX : 0)}px
    );
    width: 20px;
  }

  svg path {
    stroke: currentColor;
  }

  @media (hover: hover) {
    ${AttachmentTile}:hover &,
    ${AttachmentTile}:focus-visible & {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const ATTACHMENT_MODAL_VIEWPORT_MARGIN_PX = 16;
const ATTACHMENT_MODAL_VIEWPORT_MAX_WIDTH = `calc(100vw - ${
  2 * ATTACHMENT_MODAL_VIEWPORT_MARGIN_PX
}px)`;
const ATTACHMENT_MODAL_VIEWPORT_MAX_HEIGHT = `calc(100vh - ${
  2 * ATTACHMENT_MODAL_VIEWPORT_MARGIN_PX
}px)`;
const ATTACHMENT_MODAL_BUTTONS_HEIGHT_PX = 40;
const ATTACHMENT_MODAL_CHROME_HEIGHT_PX =
  2 * CARD_PADDING_PX + ATTACHMENT_MODAL_BUTTONS_HEIGHT_PX + CARD_GAP_PX;
const ATTACHMENT_MODAL_MEDIA_MAX_WIDTH = `calc(min(90vw, ${ATTACHMENT_MODAL_VIEWPORT_MAX_WIDTH}) - ${
  2 * CARD_PADDING_PX
}px)`;
const ATTACHMENT_MODAL_MEDIA_MAX_HEIGHT = `min(80vh, 90vh - ${ATTACHMENT_MODAL_CHROME_HEIGHT_PX}px)`;
const DEFAULT_VIDEO_ASPECT_RATIO = 16 / 9;

export const AttachmentModalDialog = styled(Dialog)`
  .MuiDialog-paper {
    background: transparent;
    box-shadow: none;
    margin: ${ATTACHMENT_MODAL_VIEWPORT_MARGIN_PX}px;
    max-height: ${ATTACHMENT_MODAL_VIEWPORT_MAX_HEIGHT};
    max-width: ${ATTACHMENT_MODAL_VIEWPORT_MAX_WIDTH};
  }
`;

export const AttachmentModalCard = styled(Card)`
  box-sizing: border-box;
  position: relative;
`;

export const AttachmentModalResizeHandle = styled('button')`
  appearance: none;
  background: none;
  border: none;
  border-bottom-right-radius: ${CARD_RADIUS_PX}px;
  bottom: 0;
  color: ${colours.neutral[300]};
  cursor: nwse-resize;
  display: block;
  height: ${CARD_RADIUS_PX}px;
  padding: 0;
  position: absolute;
  right: 0;
  touch-action: none;
  transition: color 0.15s ease;
  width: ${CARD_RADIUS_PX}px;

  svg {
    display: block;
    height: 100%;
    width: 100%;
  }

  ${AttachmentModalCard}:hover & {
    color: ${colours.neutral[400]};
  }

  ${AttachmentModalCard} &:hover,
  ${AttachmentModalCard} &:active,
  ${AttachmentModalCard} &:focus-visible {
    color: ${colours.neutral[600]};
  }

  &:focus-visible {
    outline: 2px solid ${colours.primary[500]};
    outline-offset: -2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  @media (hover: none) {
    display: none;
  }
`;

export const AttachmentModalButtons = styled('div')`
  display: flex;
  gap: 10px;
  min-height: ${ATTACHMENT_MODAL_BUTTONS_HEIGHT_PX}px;
`;

export const AttachmentModalCloseButton = styled(Buttons.SecondaryLightIcon)`
  margin-left: auto;
`;

const attachmentModalMedia = css`
  border-radius: ${ATTACHMENT_RADIUS_PX}px;
  display: block;
  object-fit: contain;
`;

export const AttachmentModalImage = styled('img')<{
  $customWidth: number | null;
}>`
  ${attachmentModalMedia}
  ${({ $customWidth }) =>
    $customWidth === null
      ? css`
          max-height: ${ATTACHMENT_MODAL_MEDIA_MAX_HEIGHT};
          max-width: ${ATTACHMENT_MODAL_MEDIA_MAX_WIDTH};
        `
      : css`
          height: auto;
          width: ${$customWidth}px;
        `}
`;

export const AttachmentModalVideo = styled('video')<{
  $aspectRatio: number | null;
  $customWidth: number | null;
  $isLoading: boolean;
}>`
  ${attachmentModalMedia}
  ${({ $isLoading }) =>
    $isLoading &&
    css`
      ${skeletonPulseAnimation}
      background: ${colours.neutral[200]};
    `}
  aspect-ratio: auto ${DEFAULT_VIDEO_ASPECT_RATIO};
  height: auto;
  width: ${({ $aspectRatio, $customWidth }) =>
    $customWidth === null
      ? `min(${ATTACHMENT_MODAL_MEDIA_MAX_WIDTH}, ${ATTACHMENT_MODAL_MEDIA_MAX_HEIGHT} * ${
          $aspectRatio ?? DEFAULT_VIDEO_ASPECT_RATIO
        })`
      : `${$customWidth}px`};
`;

/* ------------------------------------------------------------------
 * Completed form card (rendered as its own slot in the notes stack)
 * ------------------------------------------------------------------ */

export const FormCardAuthorStack = styled('div')`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const FormCardAuthor = styled(Text.LabelSemibold)`
  color: ${colours.neutral[900]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const FormCardTime = styled(Text.Label)`
  color: ${colours.neutral[500]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const FormCardPill = styled('button')`
  align-items: center;
  appearance: none;
  background: ${colours.neutral[100]};
  border: none;
  border-radius: 20px;
  cursor: pointer;
  display: flex;
  gap: 15px;
  overflow: hidden;
  padding: 15px;
  text-align: left;
  width: 100%;
`;

export const FormCardIcon = styled('div')`
  flex-shrink: 0;
  height: 60px;
  overflow: visible;
  width: 50px;

  svg {
    display: block;
    height: auto;
    margin: -10px 0 0 -15px;
    width: 83px;
  }
`;

export const FormCardText = styled('div')`
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const FormCardTitle = styled(Text.P1Bold)`
  color: ${colours.neutral[900]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

export const FormCardSubtitle = styled(Text.P1)`
  color: ${colours.neutral[600]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

export const FormCardSendIcon = styled('span')`
  align-items: center;
  color: ${colours.neutral[900]};
  display: flex;
  flex-shrink: 0;
  height: 20px;
  justify-content: center;
  padding: 10px;
  width: 20px;

  svg {
    height: 20px;
    width: 20px;
  }
`;

/* ------------------------------------------------------------------
 * Loading pill (shown while entity extraction / reflex generation is running)
 * ------------------------------------------------------------------ */

export const LoadingPill = styled('div')`
  align-items: center;
  align-self: flex-start;
  background: ${colours.neutral[100]};
  border-radius: 20px;
  box-sizing: border-box;
  color: ${colours.neutral[500]};
  display: inline-flex;
  gap: 8px;
  padding: 10px 15px;
  width: fit-content;
`;

export const MenuItemLoadingIcon = styled('span')`
  align-items: center;
  color: inherit;
  display: inline-flex;
  flex-shrink: 0;
  height: 20px;
  justify-content: center;
  width: 20px;

  svg {
    animation: ${spin} 2.5s linear infinite;
    height: 20px;
    width: 20px;
  }

  svg path {
    stroke: currentColor;
  }
`;

export const LoadingPillIcon = styled('span')`
  align-items: center;
  color: inherit;
  display: flex;
  flex-shrink: 0;
  height: 18px;
  justify-content: center;
  transform: rotate(180deg);
  width: 18px;

  svg {
    animation: ${spin} 2.5s linear infinite;
    height: 18px;
    width: 18px;
  }

  svg path {
    stroke: currentColor;
  }
`;

export const LoadingPillLabel = styled(Text.P2Semibold)`
  color: inherit;
`;

/* ------------------------------------------------------------------
 * Expandable section (Missing Information / Instant Reflex Guidance)
 * ------------------------------------------------------------------ */

export const ExpandableSection = styled('div')`
  background: ${colours.neutral[100]};
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  overflow: hidden;
  padding: 10px;
`;

export const ExpandableHeader = styled('button')<{
  $variant: 'missing-info' | 'guidance' | 'answers';
  $static?: boolean;
}>`
  align-items: center;
  appearance: none;
  background: transparent;
  border: none;
  box-sizing: border-box;
  color: ${({ $variant }) => {
    if ($variant === 'missing-info') return '#FF8800';
    if ($variant === 'answers') return colours.mint[600];
    return colours.blue[500];
  }};
  cursor: ${({ $static }) => ($static ? 'default' : 'pointer')};
  display: flex;
  font: inherit;
  gap: 5px;
  padding: 0;
  text-align: left;
  width: 100%;
`;

export const ExpandableHeaderAccessory = styled('span')`
  display: flex;
  flex-shrink: 0;
  margin-left: auto;
`;

export const ExpandableHeaderIcon = styled('span')`
  align-items: center;
  color: inherit;
  display: flex;
  flex-shrink: 0;
  height: 18px;
  justify-content: center;
  width: 18px;

  svg {
    height: 18px;
    width: 18px;
  }

  svg path {
    stroke: currentColor;
  }
`;

export const ExpandableHeaderLabel = styled(Text.P1Bold)`
  color: inherit;
  flex-shrink: 0;
  white-space: nowrap;
`;

export const ExpandableHeaderPreview = styled(Text.P1)`
  color: ${colours.neutral[600]};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ExpandableHeaderChevron = styled('span')<{ $expanded: boolean }>`
  align-items: center;
  color: ${colours.neutral[600]};
  display: flex;
  flex-shrink: 0;
  height: 20px;
  justify-content: center;
  margin-left: auto;
  transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});
  transition: transform 200ms ease;
  width: 20px;

  svg {
    height: 20px;
    width: 20px;
  }
`;

export const ExpandableBody = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

export const ExpandableQuestion = styled(Text.P2Bold)`
  color: ${colours.neutral[900]};
  width: 100%;
`;

export const ExpandableStepBody = styled(Text.P2)`
  color: ${colours.neutral[900]};
  white-space: pre-wrap;
  width: 100%;
`;

export const ExpandableErrorMessage = styled(Text.P2)`
  color: ${colours.error[500]};
  width: 100%;
`;

/* Pagination — arrow / progress / arrow */

export const Pagination = styled('div')`
  align-items: center;
  display: flex;
  gap: 15px;
  width: 100%;
`;

export const NavButton = styled(Buttons.SecondaryLightIcon)`
  flex-shrink: 0;
`;

export const SubmitButton = styled(Buttons.PrimaryIcon)`
  flex-shrink: 0;
`;

export const ApproveAllButton = styled(Buttons.Primary)`
  flex-grow: 0;
  flex-shrink: 0;
  width: fit-content;

  @media (max-width: ${breakpoints.tablet}) {
    flex-grow: 0;
    width: fit-content;
  }
`;

export const PageIndicatorTrack = styled('div')`
  align-items: center;
  display: flex;
  flex: 1 0 0;
  gap: 10px;
  justify-content: center;
  min-width: 0;
`;

export const PageIndicatorPill = styled('div')<{ $active: boolean }>`
  border-radius: 4px;
  flex-shrink: 0;
  height: 6px;
  transition: width 200ms ease, background-color 200ms ease;
  ${({ $active }) =>
    $active
      ? css`
          background-color: ${colours.neutral[900]};
          width: 40px;
        `
      : css`
          background-color: ${colours.neutral[400]};
          width: 6px;
        `}
`;

export const ProgressDotTrack = styled('div')`
  align-items: center;
  display: flex;
  flex: 1 0 0;
  gap: 5px;
  justify-content: center;
  min-width: 0;
`;

export const ProgressDotSlot = styled('div')<{ $active: boolean }>`
  align-items: center;
  background-color: ${({ $active }) =>
    $active ? colours.neutral[300] : 'transparent'};
  border-radius: 100px;
  display: flex;
  flex-shrink: 0;
  padding: 4px;
`;

export const ProgressDot = styled('div')<{
  $completed: boolean;
  $active: boolean;
}>`
  border-radius: 50%;
  box-sizing: border-box;
  flex-shrink: 0;
  height: 10px;
  width: 10px;
  transition: background-color 200ms ease, border-color 200ms ease;

  ${({ $completed, $active }) => {
    if ($completed) {
      return css`
        background-color: ${colours.mint[600]};
        border: 1.5px solid ${colours.mint[600]};
      `;
    }
    return css`
      background-color: transparent;
      border: 1.5px solid
        ${$active ? colours.neutral[500] : colours.neutral[400]};
    `;
  }}
`;

/* Inputs (mirror the shape used in MissingInfoCard, tuned for the inline pill background) */

export const BinaryRow = styled('div')`
  display: flex;
  gap: 10px;
  width: 100%;
`;

export const BinaryButton = styled(Buttons.SecondaryDark)<{
  $selected: boolean;
}>`
  flex: 1 0 0;
  max-width: 230px;
  min-width: 80px;
  width: auto;

  ${({ $selected }) =>
    !$selected &&
    css`
      background-color: transparent;
      border: 2px solid ${colours.neutral[200]};
      color: ${colours.neutral[900]};

      svg path {
        stroke: ${colours.neutral[900]};
      }

      &:hover,
      &:focus {
        background-color: transparent;
        border-color: ${colours.neutral[300]};
      }
    `}
`;

const pillInputBase = css`
  ${typography.p2}
  background-color: transparent;
  border: 2px solid ${colours.neutral[200]};
  box-sizing: border-box;
  width: 100%;
`;

export const DatePill = styled('button')`
  ${pillInputBase}
  align-items: center;
  border-radius: 30px;
  color: ${colours.neutral[900]};
  cursor: pointer;
  display: flex;
  gap: 10px;
  padding: 15px 15px 15px 20px;
  text-align: left;

  &:hover,
  &:focus {
    border-color: ${colours.neutral[300]};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

export const DatePillValue = styled('span')<{ $isPlaceholder: boolean }>`
  color: ${({ $isPlaceholder }) =>
    $isPlaceholder ? colours.neutral[500] : colours.neutral[900]};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const DatePillIcon = styled('span')`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  height: 20px;
  justify-content: center;
  width: 20px;

  svg {
    height: 20px;
    width: 20px;
  }

  svg path {
    stroke: ${colours.neutral[900]};
  }
`;

export const TextArea = styled('textarea')`
  ${pillInputBase}
  border-radius: 25px;
  color: ${colours.neutral[900]};
  display: block;
  height: 120px;
  padding: 15px 20px;
  resize: none;

  &::placeholder {
    color: ${colours.neutral[500]};
  }

  &:focus {
    border-color: ${colours.neutral[300]};
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

export const DisabledInputWrapper = styled('span')`
  cursor: not-allowed;
  display: block;
  width: 100%;
`;

export const EmptyReflexDescription = styled(Text.P2)`
  color: ${colours.neutral[600]};
  margin-bottom: 12px;
`;

/* Guidance step body wrapper */

export const StepTitle = styled(Text.P2Bold)`
  color: ${colours.neutral[900]};
  width: 100%;
`;

/* Shared guidance-step action UI (SecondaryLight pills, section labels) */

export const StepSectionLabel = styled(Text.P1Bold)`
  color: ${colours.neutral[600]};
`;

export const StepActionRow = styled('div')`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  width: 100%;
`;

export const StepActionColumn = styled('div')`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
`;

export const StepActionPill = styled(Buttons.SecondaryLight).attrs({
  $size: 'small' as const,
})<{ $isDisabled?: boolean }>`
  gap: 5px;
  min-height: 0;
  padding: 6px 10px;
  width: fit-content;
  flex-grow: 0;
  justify-content: flex-start;
  text-align: left;

  ${({ $isDisabled }: { $isDisabled?: boolean }) =>
    $isDisabled &&
    css`
      opacity: 0.6;
      pointer-events: none;
    `}

  svg {
    height: 18px;
    width: 18px;
  }
`;

export const HiddenFileInput = styled('input')`
  display: none;
`;

export const StepErrorMessage = styled(Text.P2)`
  color: ${colours.error[500]};
  width: 100%;
`;

/* ------------------------------------------------------------------
 * Reflex feedback (Instant Reflex Specialists)
 * ------------------------------------------------------------------ */

export const FeedbackRow = styled('div')`
  align-items: center;
  display: flex;
  gap: 10px;
  width: 100%;
`;

export const FeedbackThumbButton = styled('button')<{ $selected: boolean }>`
  align-items: center;
  appearance: none;
  background: ${({ $selected }) =>
    $selected ? colours.neutral[900] : 'transparent'};
  border: none;
  border-radius: 50%;
  color: ${({ $selected }) =>
    $selected ? colours.shade[0] : colours.neutral[900]};
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 30px;
  justify-content: center;
  padding: 0;
  width: 30px;

  svg {
    height: 18px;
    width: 18px;
  }

  svg path {
    stroke: currentColor;
  }
`;

export const FeedbackResult = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 0;
  width: 100%;
`;

export const FeedbackResultIcon = styled('span')`
  align-items: center;
  color: ${colours.mint[600]};
  display: flex;
  height: 24px;
  justify-content: center;
  width: 24px;

  svg {
    height: 24px;
    width: 24px;
  }

  svg path {
    stroke: currentColor;
  }
`;

export const FeedbackResultText = styled(Text.P2)`
  color: ${colours.neutral[900]};
  text-align: center;
`;

export const VersionMenuWrapper = styled('span')`
  & > div {
    min-width: 0;
    min-height: 0;
  }
`;

export const VersionTrigger = styled('button')`
  align-items: center;
  appearance: none;
  background: transparent;
  border: none;
  color: ${colours.neutral[500]};
  cursor: pointer;
  display: flex;
  gap: 2px;
  padding: 0;

  svg {
    height: 20px;
    width: 20px;
  }

  svg path {
    stroke: currentColor;
  }
`;

export const VersionTriggerLabel = styled(Text.P1Bold)`
  color: inherit;
`;

/* Compose-message step body (one shade darker than the expandable section) */

export const ComposeMessageBody = styled('div')`
  background-color: ${colours.neutral[200]};
  border-radius: 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding: 10px;
  width: 100%;
`;

export const ComposeMessageBodyText = styled(Text.P2)`
  overflow-wrap: anywhere;
  white-space: pre-wrap;
`;

export const AttachmentDocumentList = styled('ul')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const AttachmentDocumentRow = styled('li')`
  align-items: center;
  background: ${colours.neutral[100]};
  border-radius: ${ATTACHMENT_RADIUS_PX}px;
  display: flex;
  gap: 8px;
  min-height: 50px;
  padding: 0 8px 0 14px;
`;

export const AttachmentDocumentLink = styled('a')`
  align-items: center;
  color: ${colours.neutral[900]};
  display: flex;
  flex: 1;
  gap: 10px;
  min-width: 0;
  padding: 12px 0;
  text-decoration: none;

  svg {
    color: ${colours.neutral[600]};
    flex-shrink: 0;
    height: 20px;
    width: 20px;
  }

  &:focus-visible {
    outline: 2px solid ${colours.primary[500]};
    outline-offset: 2px;
  }
`;

export const AttachmentDocumentName = styled(Text.P2Semibold)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
